"use client";

import { motion } from "src/lib/motion";
import { Icons } from "@/components/icons";
import { useNextSectionScroll } from "@/lib/hooks/use-next-section-scroll";
import { useTranslations } from "@/lib/hooks/use-translations";
import HeroVideoDialog from "@/components/magicui/hero-video";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import Link from "next/link";
import videoEn from "@/assets/videos/09-01-001-LandingEnglish_optimized.mp4";
import videoDe from "@/assets/videos/00-002-Landingpage_1920x1080.mp4";
import { useEffect, useState } from "react";

const ease = [0.16, 1, 0.3, 1];

function HeroTitles() {
  const { t } = useTranslations();
  const titleWords = t<string[]>("pages.home.hero.title");
  const [isAnimating, setIsAnimating] = useState(false);
  const [animatedIndexes, setAnimatedIndexes] = useState<number[]>([]);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined") {
      const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      setPrefersReducedMotion(mediaQuery.matches);
    }
    titleWords.forEach((_, index) => {
      const delay = index * 200;
      setTimeout(() => {
        setAnimatedIndexes((prev) =>
          prev.includes(index) ? prev : [...prev, index]
        );
      }, delay);
    });
  }, [titleWords]);
  return (
    <div className="flex w-full max-w-2xl flex-col space-y-4 overflow-hidden pt-8">
      <motion.h1
        className="text-center text-4xl font-medium leading-tight text-foreground sm:text-5xl md:text-6xl"
        initial={{ filter: "blur(10px)", opacity: 1, y: 30 }}
        animate={{ filter: "blur(0px)", opacity: 1, y: 0 }}
        transition={{
          duration: 1,
          ease,
          staggerChildren: 0.2,
        }}
      >
        {titleWords.map((text, index) => (
          <motion.span
            key={index}
            className={`inline-block paragraph relative px-1 md:px-2 text-balance font-semibold ${
              animatedIndexes.includes(index) ? "is-animating" : ""
            }`}
            initial={{ y: 15 }}
            animate={{ y: 0 }}
            transition={{
              duration: 0.8,
              delay: index * 0.2,
              ease,
            }}
          >
            {text}
          </motion.span>
        ))}
      </motion.h1>
      <motion.p
        className={`paragraph relative mx-auto max-w-xl text-center text-lg leading-7 text-muted-foreground sm:text-xl sm:leading-9 text-balance ${
          isAnimating ? "is-animating" : ""
        }`}
        initial={prefersReducedMotion ? false : { y: 20 }}
        animate={prefersReducedMotion ? false : { y: 0 }}
        transition={{
          delay: 0,
          duration: 0.8,
          ease,
        }}
        onAnimationStart={() => setIsAnimating(true)}
      >
        {t("pages.home.hero.subtitle")}
      </motion.p>
    </div>
  );
}

function HeroCTA({ locale = "en" }: { locale?: "de" | "en" | "zh-CN" } = {}) {
  const handleNextSection = useNextSectionScroll("hero");
  const { t, locale: currentLocale } = useTranslations();

  // Determine contact page URL based on locale
  const getContactUrl = () => {
    const localeToUse = currentLocale || locale;
    if (localeToUse === "de") return "/de/kontakt";
    if (localeToUse === "zh-CN") return "/zh-CN/contact";
    return "/contact"; // default to English
  };

  return (
    <>
      <motion.div
        className="mx-auto mt-6 flex w-full max-w-2xl flex-col items-start justify-center space-y-4 sm:flex-row sm:space-x-4 sm:space-y-0"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.8, ease }}
      >
        <Link
          href={getContactUrl()}
          className={cn(
            buttonVariants({ variant: "default" }),
            "w-auto text-background flex gap-2 max-md:w-full"
          )}
        >
          <Icons.logoIcon className="h-6 w-6" />
          {t("common.buttons.getStarted")}
        </Link>
        <button
          onClick={handleNextSection}
          className={cn(
            buttonVariants({ variant: "outline" }),
            "w-auto max-md:w-full"
          )}
        >
          {t("pages.home.hero.cta.readMore")}
        </button>
      </motion.div>
    </>
  );
}

function HeroImage({ locale = "en" }: { locale?: "de" | "en" | "zh-CN" } = {}) {
  const [isAnimating, setIsAnimating] = useState(false);
  return (
    <motion.div
      className={`relative mx-auto flex w-full items-center justify-center  ${
        isAnimating ? "is-animating overflow-visible" : ""
      }`}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.2, duration: 1, ease }}
      onAnimationStart={() =>
        setTimeout(() => {
          setIsAnimating(true);
        }, 1200)
      }
    >
      <div className="w-full max-w-[90vw] lg:max-w-screen-lg">
        <HeroVideoDialog
          animationStyle="from-center"
          videoSrc={locale === "en" ? videoEn : videoDe}
          thumbnailSrc="/video-image.jpg"
          thumbnailAlt="SavagesCorp AI Solutions Dashboard Overview - Interactive demonstration of our AI-powered automation platform"
          className="border rounded-lg shadow-lg mt-16"
        />
      </div>
    </motion.div>
  );
}

export default function Hero2({
  locale = "en",
}: { locale?: "de" | "en" | "zh-CN" } = {}) {
  return (
    <section id="hero">
      <div className="relative flex w-full flex-col items-center justify-start px-6 pt-16 sm:px-6 sm:pt-20 md:pt-32 lg:px-6">
        <HeroTitles />
        <HeroCTA locale={locale} />
        <HeroImage locale={locale} />
        <div className="pointer-events-none absolute inset-x-0 -bottom-12 max-md:h-1/5 h-1/3 bg-gradient-to-t from-background via-background to-transparent lg:h-1/4"></div>
      </div>
    </section>
  );
}
